const BARE_URL_PATTERN = /https?:\/\/[^\s<]+/g;

type ShouldHandleEditorPasteParams = {
  disabled: boolean;
  htmlContent: string;
  plainText: string;
  shouldRemovePastedImages: boolean;
};

const HTML_IMAGE_TAG = /<img\b/i;
const HTML_IMAGE_TAG_GLOBAL = /<img\b[^>]*>/gi;

export const stripImagesFromPastedHtml = (html: string): string =>
  html.replace(HTML_IMAGE_TAG_GLOBAL, '');

export const pastedHtmlContainsImages = (html: string): boolean => HTML_IMAGE_TAG.test(html);

export const shouldHandleEditorPaste = ({
  disabled,
  htmlContent,
  plainText
}: ShouldHandleEditorPasteParams): boolean => {
  if (disabled) return false;
  if (htmlContent) return false;

  return Boolean(plainText);
};

const createLinkElement = (url: string, doc: Document): HTMLAnchorElement => {
  const anchor = doc.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.textContent = url;

  return anchor;
};

const linkifyTextToNodes = (text: string, doc: Document): DocumentFragment => {
  const fragment = doc.createDocumentFragment();
  BARE_URL_PATTERN.lastIndex = 0;

  let lastIndex = 0;
  let match = BARE_URL_PATTERN.exec(text);

  while (match) {
    const url = match[0];
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      fragment.appendChild(doc.createTextNode(text.slice(lastIndex, matchIndex)));
    }

    fragment.appendChild(createLinkElement(url, doc));
    lastIndex = matchIndex + url.length;
    match = BARE_URL_PATTERN.exec(text);
  }

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  return fragment;
};

const linkifyUrls = (text: string): string => {
  const doc = new DOMParser().parseFromString('', 'text/html');
  const container = doc.createElement('div');
  container.appendChild(linkifyTextToNodes(text, doc));

  return container.innerHTML;
};

const textContainsBareUrl = (text: string): boolean => {
  BARE_URL_PATTERN.lastIndex = 0;
  return BARE_URL_PATTERN.test(text);
};

const isInsideLink = (node: Text): boolean => {
  let parent = node.parentNode;

  while (parent) {
    if (parent instanceof HTMLElement && parent.tagName === 'A') {
      return true;
    }

    parent = parent.parentNode;
  }

  return false;
};

export const preparePastedEditorContent = (htmlContent: string, plainText: string): string => {
  if (!htmlContent) {
    return linkifyUrls(plainText);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');

  doc.querySelectorAll('img').forEach(img => img.remove());

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  let node = walker.nextNode();
  while (node) {
    if (node instanceof Text && node.textContent) {
      if (!isInsideLink(node) && textContainsBareUrl(node.textContent)) {
        textNodes.push(node);
      }
    }

    node = walker.nextNode();
  }

  textNodes.forEach(textNode => {
    const parent = textNode.parentNode;
    if (!parent || !textNode.textContent) return;

    const linkifiedNodes = linkifyTextToNodes(textNode.textContent, doc);

    while (linkifiedNodes.firstChild) {
      parent.insertBefore(linkifiedNodes.firstChild, textNode);
    }

    parent.removeChild(textNode);
  });

  return doc.body.innerHTML;
};
